<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ConversationApi from 'dashboard/api/inbox/conversation';

const props = defineProps({
  show: { type: Boolean, default: false },
  conversationId: { type: [Number, String], required: true },
  currentInboxId: { type: [Number, String], required: true },
  currentInboxChannelType: { type: String, required: true },
});

const emit = defineEmits(['cancel', 'update:show', 'transferred']);

const { t } = useI18n();
const store = useStore();

const selectedInboxId = ref('');
const confirmText = ref('');
const submitting = ref(false);

const localShow = computed({
  get: () => props.show,
  set: value => emit('update:show', value),
});

const allInboxes = computed(() => store.getters['inboxes/getInboxes'] || []);

const eligibleInboxes = computed(() =>
  allInboxes.value.filter(
    inbox =>
      inbox.channel_type === props.currentInboxChannelType &&
      Number(inbox.id) !== Number(props.currentInboxId)
  )
);

const selectedInbox = computed(() =>
  eligibleInboxes.value.find(
    i => Number(i.id) === Number(selectedInboxId.value)
  )
);

const canTransfer = computed(
  () =>
    !!selectedInbox.value &&
    confirmText.value.trim() === selectedInbox.value.name &&
    !submitting.value
);

async function performTransfer() {
  if (!canTransfer.value) return;
  submitting.value = true;
  try {
    await ConversationApi.transferInbox({
      conversationId: props.conversationId,
      inboxId: selectedInbox.value.id,
    });
    useAlert(
      t('CONVERSATION.TRANSFER_INBOX.SUCCESS', {
        inbox: selectedInbox.value.name,
      })
    );
    emit('transferred', selectedInbox.value.id);
    emit('cancel');
  } catch (error) {
    const message =
      error?.response?.data?.error || t('CONVERSATION.TRANSFER_INBOX.ERROR');
    useAlert(message);
  } finally {
    submitting.value = false;
  }
}

function onCancel() {
  emit('cancel');
}
</script>

<template>
  <woot-modal v-model:show="localShow" :on-close="onCancel">
    <div class="flex flex-col h-auto overflow-auto p-6 gap-4">
      <woot-modal-header
        :header-title="$t('CONVERSATION.TRANSFER_INBOX.TITLE')"
        :header-content="$t('CONVERSATION.TRANSFER_INBOX.DESCRIPTION')"
      />

      <div
        class="bg-n-amber-3 border border-n-amber-6 rounded-md p-3 text-sm text-n-slate-12"
      >
        {{ $t('CONVERSATION.TRANSFER_INBOX.WARNING') }}
      </div>

      <div v-if="eligibleInboxes.length === 0" class="text-sm text-n-slate-11">
        {{ $t('CONVERSATION.TRANSFER_INBOX.NO_ELIGIBLE_INBOX') }}
      </div>

      <div v-else class="flex flex-col gap-3">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('CONVERSATION.TRANSFER_INBOX.SELECT_LABEL') }}
        </label>
        <select
          v-model="selectedInboxId"
          class="text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-background"
        >
          <option value="">
            {{ $t('CONVERSATION.TRANSFER_INBOX.SELECT_PLACEHOLDER') }}
          </option>
          <option
            v-for="inbox in eligibleInboxes"
            :key="inbox.id"
            :value="inbox.id"
          >
            {{ inbox.name }}
          </option>
        </select>

        <template v-if="selectedInbox">
          <label class="text-sm font-medium text-n-slate-12 mt-2">
            {{
              $t('CONVERSATION.TRANSFER_INBOX.CONFIRM_LABEL', {
                inbox: selectedInbox.name,
              })
            }}
          </label>
          <input
            v-model="confirmText"
            type="text"
            :placeholder="selectedInbox.name"
            class="text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-background"
          />
        </template>
      </div>

      <div class="flex justify-end gap-2 mt-2">
        <NextButton
          faded
          slate
          :label="$t('CONVERSATION.TRANSFER_INBOX.CANCEL')"
          :disabled="submitting"
          @click="onCancel"
        />
        <NextButton
          :label="$t('CONVERSATION.TRANSFER_INBOX.CONFIRM_BUTTON')"
          :disabled="!canTransfer"
          :is-loading="submitting"
          @click="performTransfer"
        />
      </div>
    </div>
  </woot-modal>
</template>
